import { Cancel, Notifications, Videocam } from '@mui/icons-material';
import React from 'react';

import { Colors } from 'src/common/ui/base';
import { Badge } from 'src/common/ui/components/Badge';
import { Button } from 'src/common/ui/components/Button';
import {
  StatusPromptActions,
  StatusPromptBody,
  StatusPromptBodyText,
  StatusPromptCard,
  StatusPromptHeadline,
  StatusPromptIcon,
} from 'src/common/ui/components/StatusPromptCard/StatusPromptCard';
import { AutoCancelCountdown } from 'src/components/pages/pageElements/videoConference/components/AutoCancelCountdown';
import { formatTimestampDifference } from 'src/components/pages/pageElements/videoConference/domain/formatTimestampDifference';
import { ReactComponent as IndividualIconBlue } from 'src/shared/icons/iconIndividualBlue.svg';
import { ReactComponent as MedicalIcon } from 'src/shared/icons/iconMedicalBlue.svg';
import { ReactComponent as SupportIcon } from 'src/shared/icons/iconSupport.svg';
import { ReactComponent as WaitingIcon } from 'src/shared/icons/iconWaiting.svg';

type PatientNotInVisitProps = {
  isProviderInVisit: boolean;
  onJoinVisit: () => void;
  isOverridden: boolean;
  autoCancelAt?: number;
  onOverride: () => Promise<void>;
  eventStart: number;
  hasSessionOverlapped?: boolean;
  isAutoCancelPrevented: boolean;
  currentTime: number;
};

export const PatientNotInVisit = ({
  isProviderInVisit,
  onJoinVisit,
  autoCancelAt,
  isOverridden,
  onOverride,
  eventStart,
  hasSessionOverlapped = false,
  isAutoCancelPrevented,
  currentTime,
}: PatientNotInVisitProps) => {
  const showVisitInProgress = hasSessionOverlapped || isAutoCancelPrevented;
  const icon = showVisitInProgress ? MedicalIcon : WaitingIcon;

  let headline: React.ReactNode;
  if (showVisitInProgress) {
    headline = 'Visit in progress';
  } else if (isProviderInVisit) {
    headline = 'Waiting for patient';
  } else {
    headline = (
      <>
        Please join
        <br />
        when you&apos;re ready
      </>
    );
  }

  const bodyContent = showVisitInProgress ? (
    <StatusPromptBodyText
      text={
        <>
          <b>{formatTimestampDifference(eventStart || 0, currentTime)}</b> since visit was accepted
        </>
      }
    />
  ) : (
    <>
      <StatusPromptBodyText
        text={
          <>
            The patient has been notified
            <br /> by text that their visit is starting.
          </>
        }
        icon={Notifications}
        iconColor={Colors.BlueSpruce}
      />
      <AutoCancelCountdown
        currentTime={currentTime}
        autoCancelAt={autoCancelAt}
        showAutoCancelOverride={!isOverridden}
        onOverride={onOverride}
      />
    </>
  );

  return (
    <StatusPromptCard>
      <StatusPromptIcon icon={icon} />
      <StatusPromptHeadline headline={headline} />
      <Badge icon="infoOutlined" rounded color="info" label="Patient not in visit" />
      <StatusPromptBody>{bodyContent}</StatusPromptBody>
      {!isProviderInVisit && (
        <StatusPromptActions>
          <Button
            onClick={onJoinVisit}
            variant="contained"
            color="primary"
            startIcon={<Videocam />}
          >
            Join Visit
          </Button>
        </StatusPromptActions>
      )}
    </StatusPromptCard>
  );
};

export const PatientInVisit = ({ onJoinVisit }: { onJoinVisit: () => void }) => {
  return (
    <StatusPromptCard>
      <StatusPromptIcon icon={IndividualIconBlue} />
      <StatusPromptHeadline headline="Please join when you're ready" />
      <Badge icon="check" rounded color="success" label="Patient in visit" />
      <StatusPromptActions>
        <Button onClick={onJoinVisit} variant="contained" color="primary" startIcon={<Videocam />}>
          Join Visit
        </Button>
      </StatusPromptActions>
    </StatusPromptCard>
  );
};

export const PatientMissedVisit = ({
  onClose,
  autoCancelInProgress,
}: {
  onClose: () => void;
  autoCancelInProgress: boolean;
}) => {
  return (
    <StatusPromptCard variant="high-urgency">
      <StatusPromptIcon icon={SupportIcon} />
      <StatusPromptHeadline headline="Patient missed the visit" />
      <StatusPromptBodyText
        text={`This visit ${autoCancelInProgress ? 'is being' : 'was'} auto-canceled`}
        icon={Cancel}
        iconColor={Colors.Peach5}
      />
      <StatusPromptBodyText text="You may now close this tab and return to My Day Today" />
      <StatusPromptActions>
        <Button onClick={onClose} variant="contained" color="urgent">
          Close
        </Button>
      </StatusPromptActions>
    </StatusPromptCard>
  );
};
